import { classifyEqualVsCustomSplit, getShareFraction, reconcileFullCustomShareRemainder } from '../services/itemShares';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, Lock, SlidersHorizontal, Users, X } from 'lucide-react';
import { WebHeaderIconButton } from './WebHeaderIconButton';
import { normalizePhone } from '../services/firebase';
import { WebItemIconAvatar } from './WebItemIconAvatar';
import { resolveWebParticipantIdentity } from '../utils/webParticipantAvatar';
import { useSheetLifecycle } from '../hooks/useSheetLifecycle';
import { triggerUiHaptic } from '../utils/webHaptics';

export type WhoHadThisParticipant = {
    id: string;
    name: string;
    phone?: string;
    normalizedPhone?: string;
    photoURL?: string;
    isOrganizer?: boolean;
    isLocked?: boolean;
    isPaymentLocked?: boolean;
    isDeclined?: boolean;
    isPaid?: boolean;
    status?: string;
    uid?: string;
    userId?: string;
    participantUid?: string;
    participantUserUid?: string;
};

export const getWhoHadThisParticipantAliases = (p: WhoHadThisParticipant, normOrgPhone?: string): string[] => {
    const aliases = new Set<string>();
    if (p.phone) {
        aliases.add(p.phone);
        const norm = normalizePhone(p.phone);
        if (norm) aliases.add(norm);
    }
    if (p.normalizedPhone) {
        aliases.add(p.normalizedPhone);
    }
    if (p.id) {
        aliases.add(p.id);
        const norm = normalizePhone(p.id);
        if (norm) aliases.add(norm);
    }
    if (p.uid) aliases.add(p.uid);
    if (p.userId) aliases.add(p.userId);
    if (p.participantUid) aliases.add(p.participantUid);
    if (p.participantUserUid) aliases.add(p.participantUserUid);
    if (p.isOrganizer) {
        aliases.add('organizer');
        aliases.add('Organizer');
        if (normOrgPhone) aliases.add(normOrgPhone);
    }
    return Array.from(aliases);
};

export const getWhoHadThisParticipantCanonicalKey = (p: WhoHadThisParticipant, normOrgPhone?: string): string => {
    if (p.isOrganizer) return normOrgPhone || normalizePhone(p.phone || p.id) || 'organizer';
    return normalizePhone(p.phone || p.id) || p.phone || p.id;
};

export const matchesWhoHadThisParticipant = (p: WhoHadThisParticipant, key: string, normOrgPhone?: string): boolean => {
    if (!key) return false;
    const cleanKey = String(key).trim();
    const normKey = normalizePhone(cleanKey);
    const aliases = getWhoHadThisParticipantAliases(p, normOrgPhone);
    if (aliases.includes(cleanKey)) return true;
    if (normKey && aliases.some((a) => normalizePhone(a) === normKey)) return true;
    return false;
};

export type WebWhoHadThisSheetProps = {
    visible: boolean;
    onClose: () => void;
    item: {
        id: string;
        name: string;
        amount: number;
        iconKey?: string;
        shares?: Record<string, { fraction: number; name?: string; mode?: string; isLocked?: boolean }>;
    } | null;
    participants: WhoHadThisParticipant[];
    viewerPhone?: string;
    viewerDisplayName?: string;
    viewerPhotoURL?: string | null;
    organizerPhone?: string;
    organizerName?: string;
    organizerPhotoURL?: string | null;
    session?: any;
    onSaveAssignment: (input: {
        itemId: string;
        shareMap: Record<string, { fraction: number; name?: string; mode: 'equal' | 'custom'; isLocked?: boolean }>;
    }) => void;
    getItemIconEmoji?: (iconKey?: string, name?: string) => string;
};

const formatShortName = (name: string, isViewer: boolean = false): string => {
    if (isViewer) return 'You';
    const clean = String(name || '').trim();
    if (!clean) return 'Friend';
    const parts = clean.split(/\s+/).filter(Boolean);
    if (parts.length <= 1) return parts[0] || 'Friend';
    return `${parts[0]} ${parts[parts.length - 1][0]}.`;
};

export const WebWhoHadThisSheet: React.FC<WebWhoHadThisSheetProps> = ({
    visible,
    onClose,
    item,
    participants,
    viewerPhone,
    viewerDisplayName,
    viewerPhotoURL,
    organizerPhone,
    organizerName,
    organizerPhotoURL,
    session,
    onSaveAssignment,
}) => {
    // Keep reference to last active item so bottom sheet can slide down gracefully
    const lastItemRef = useRef(item);
    if (item) {
        lastItemRef.current = item;
    }
    const currentItem = item || lastItemRef.current;
    const itemAmount = Math.max(0, Number(currentItem?.amount || 0));
    const normViewer = normalizePhone(viewerPhone || '');
    const normOrg = normalizePhone(organizerPhone || '');

    const [isCustomMode, setIsCustomMode] = useState<boolean>(false);
    const [selectedPhoneSet, setSelectedPhoneSet] = useState<Set<string>>(new Set());
    const [customDollarAmounts, setCustomDollarAmounts] = useState<Record<string, string>>({});
    const [alertModal, setAlertModal] = useState<{ title: string; message: string } | null>(null);

    // Sub-view state for editing a single participant's custom amount (Hero Amount Editor)
    const [editingParticipant, setEditingParticipant] = useState<WhoHadThisParticipant | null>(null);
    const [editingAmountText, setEditingAmountText] = useState('');
    const amountInputRef = useRef<HTMLInputElement | null>(null);

    const handleSheetDismissed = useCallback(() => {
        setEditingParticipant(null);
        setAlertModal(null);
        onClose();
    }, [onClose]);

    const { isRendered, isClosing, handleClose: handleSafeClose } = useSheetLifecycle(visible, handleSheetDismissed, 220);

    const prevVisibleRef = useRef(false);
    const prevItemIdRef = useRef('');

    const isParticipantInSelectedSet = useCallback((p: WhoHadThisParticipant, set: Set<string>) => {
        const canonical = getWhoHadThisParticipantCanonicalKey(p, normOrg);
        if (set.has(canonical)) return true;
        const aliases = getWhoHadThisParticipantAliases(p, normOrg);
        return aliases.some((a) => set.has(a));
    }, [normOrg]);

    // Initialize state when sheet opens or item changes
    useEffect(() => {
        if (!visible || !item) {
            prevVisibleRef.current = false;
            return;
        }

        const isNewlyOpened = !prevVisibleRef.current && visible;
        const isDifferentItem = prevItemIdRef.current !== String(item.id || '');
        prevVisibleRef.current = visible;
        prevItemIdRef.current = String(item.id || '');

        if (!isNewlyOpened && !isDifferentItem) {
            return;
        }

        setEditingParticipant(null);
        setAlertModal(null);

        const shares = item.shares || {};
        const isCustom = Object.values(shares).some((s: any) => s?.mode === 'custom')
            || (Object.values(shares).some(value => typeof value === 'number') && classifyEqualVsCustomSplit(shares) === 'custom');
        setIsCustomMode(isCustom);

        const set = new Set<string>();
        const map: Record<string, string> = {};
        Object.entries(shares).forEach(([key, val]: [string, any]) => {
            const frac = getShareFraction(val);
            if (frac > 0.0001) {
                const matchedP = participants.find((p) => matchesWhoHadThisParticipant(p, key, normOrg));
                const canonicalKey = matchedP ? getWhoHadThisParticipantCanonicalKey(matchedP, normOrg) : (normalizePhone(key) || key);
                set.add(canonicalKey);
                map[canonicalKey] = (frac * itemAmount).toFixed(2);
            }
        });
        if (set.size === 0 && normViewer) {
            const matchedViewer = participants.find((p) => matchesWhoHadThisParticipant(p, normViewer, normOrg));
            const canonicalViewer = matchedViewer ? getWhoHadThisParticipantCanonicalKey(matchedViewer, normOrg) : normViewer;
            set.add(canonicalViewer);
            map[canonicalViewer] = itemAmount.toFixed(2);
        }
        setSelectedPhoneSet(set);
        setCustomDollarAmounts(map);
    }, [visible, item, itemAmount, normOrg, normViewer, participants]);

    const isParticipantLocked = useCallback((p: WhoHadThisParticipant) => {
        if (!p) return false;
        if (
            p.isLocked ||
            p.isPaymentLocked ||
            p.isPaid ||
            p.status === 'paid' ||
            p.status === 'processing' ||
            p.status === 'settled_offline' ||
            p.status === 'verified'
        ) {
            return true;
        }
        const aliases = getWhoHadThisParticipantAliases(p, normOrg);
        const shares = item?.shares || {};
        return aliases.some((a) => shares[a]?.isLocked || shares[a]?.paid === true);
    }, [item?.shares, normOrg]);

    const isParticipantDeclined = useCallback((p: WhoHadThisParticipant) => {
        return Boolean(p?.isDeclined || p?.status === 'declined' || p?.status === 'declined_recurring');
    }, []);

    const lockedPhoneSet = useMemo(() => {
        const set = new Set<string>();
        participants.forEach((p) => {
            if (isParticipantLocked(p) || isParticipantDeclined(p)) {
                const canonicalKey = getWhoHadThisParticipantCanonicalKey(p, normOrg);
                set.add(canonicalKey);
            }
        });
        return set;
    }, [isParticipantDeclined, isParticipantLocked, normOrg, participants]);

    const sortedParticipants = useMemo(() => {
        return [...participants].sort((a, b) => {
            const aLocked = isParticipantLocked(a) || isParticipantDeclined(a);
            const bLocked = isParticipantLocked(b) || isParticipantDeclined(b);
            if (aLocked && !bLocked) return 1;
            if (!aLocked && bLocked) return -1;
            return 0;
        });
    }, [isParticipantDeclined, isParticipantLocked, participants]);

    const getLockedParticipantFraction = useCallback((p: WhoHadThisParticipant) => {
        if (!p) return 0;
        const aliases = getWhoHadThisParticipantAliases(p, normOrg);
        const shares = item?.shares || {};
        for (const a of aliases) {
            if (shares[a] !== undefined) {
                return Math.max(0, getShareFraction(shares[a]));
            }
        }
        return 0;
    }, [item?.shares, normOrg]);

    const selectedParticipants = useMemo(() => {
        const selected = participants.filter((p) => isParticipantInSelectedSet(p, selectedPhoneSet));
        return selected.sort((a, b) => {
            const aLocked = isParticipantLocked(a);
            const bLocked = isParticipantLocked(b);
            if (aLocked && !bLocked) return 1;
            if (!aLocked && bLocked) return -1;
            return 0;
        });
    }, [isParticipantInSelectedSet, isParticipantLocked, participants, selectedPhoneSet]);

    const lockedParticipants = useMemo(() => {
        return selectedParticipants.filter((p) => isParticipantLocked(p));
    }, [isParticipantLocked, selectedParticipants]);

    const editableParticipants = useMemo(() => {
        return selectedParticipants.filter((p) => !isParticipantLocked(p));
    }, [isParticipantLocked, selectedParticipants]);

    const totalCents = Math.round(itemAmount * 100);

    const hiddenLockedCents = useMemo(() => {
        let sum = 0;
        Object.entries(item?.shares || {}).forEach(([key, share]: [string, any]) => {
            const matched = participants.find((p) => matchesWhoHadThisParticipant(p, key, normOrg));
            if (matched && isParticipantInSelectedSet(matched, selectedPhoneSet)) return;
            const locked = matched ? isParticipantLocked(matched) : Boolean(share?.isLocked || share?.paid);
            if (locked) sum += Math.round(getShareFraction(share) * totalCents);
        });
        return sum;
    }, [isParticipantInSelectedSet, isParticipantLocked, item?.shares, normOrg, participants, selectedPhoneSet, totalCents]);

    const lockedCentsTotal = useMemo(() => {
        let sum = hiddenLockedCents;
        lockedParticipants.forEach((p) => {
            const frac = getLockedParticipantFraction(p);
            sum += Math.round(frac * totalCents);
        });
        return Math.min(totalCents, sum);
    }, [getLockedParticipantFraction, hiddenLockedCents, lockedParticipants, totalCents]);

    const lockedDollarsTotal = useMemo(() => {
        return Number((lockedCentsTotal / 100).toFixed(2));
    }, [lockedCentsTotal]);

    const remainingDollars = useMemo(() => {
        return Math.max(0, Number(((totalCents - lockedCentsTotal) / 100).toFixed(2)));
    }, [lockedCentsTotal, totalCents]);

    const equalShareAmount = useMemo(() => {
        if (editableParticipants.length === 0) {
            return lockedParticipants.length > 0 ? lockedDollarsTotal : 0;
        }
        return Number((remainingDollars / editableParticipants.length).toFixed(2));
    }, [editableParticipants.length, lockedDollarsTotal, lockedParticipants.length, remainingDollars]);

    const equalSplitSubtitle = useMemo(() => {
        if (selectedParticipants.length === 0) return '';
        if (lockedParticipants.length > 0) {
            if (editableParticipants.length > 0) {
                const countStr = `${editableParticipants.length} ${editableParticipants.length === 1 ? 'person' : 'people'}`;
                return `${countStr} · $${equalShareAmount.toFixed(2)} each ($${lockedDollarsTotal.toFixed(2)} paid)`;
            }
            return `$${lockedDollarsTotal.toFixed(2)} paid by ${lockedParticipants.length} ${lockedParticipants.length === 1 ? 'person' : 'people'}`;
        }
        const countStr = `${selectedParticipants.length} ${selectedParticipants.length === 1 ? 'person' : 'people'}`;
        return `${countStr} · $${equalShareAmount.toFixed(2)} each`;
    }, [editableParticipants.length, equalShareAmount, lockedDollarsTotal, lockedParticipants.length, selectedParticipants.length]);

    const handleToggleParticipant = (participant: WhoHadThisParticipant) => {
        if (isParticipantDeclined(participant)) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({
                title: 'Participant Declined',
                message: `${participant.name || 'This participant'} declined this request and cannot be assigned to items.`,
            });
            return;
        }
        if (isParticipantLocked(participant)) {
            triggerUiHaptic('notificationWarning');
            const isSel = isParticipantInSelectedSet(participant, selectedPhoneSet);
            setAlertModal({
                title: 'Participant Locked',
                message: isSel
                    ? `${participant.name || 'This participant'} has already paid and cannot be removed from this item.`
                    : `${participant.name || 'This participant'} has already paid and cannot be added to new items.`,
            });
            return;
        }

        const canonicalKey = getWhoHadThisParticipantCanonicalKey(participant, normOrg);
        const isCurrentlySelected = isParticipantInSelectedSet(participant, selectedPhoneSet);
        const willSelect = !isCurrentlySelected;

        // If item is already 100% locked by paid participants, block adding new people
        if (willSelect && remainingDollars <= 0.001 && lockedParticipants.length > 0) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({
                title: 'Item Fully Paid',
                message: 'This item has already been paid for in full by locked participants and cannot be joined.',
            });
            return;
        }

        triggerUiHaptic('selection');
        setSelectedPhoneSet((prev) => {
            const next = new Set(prev);
            const aliases = getWhoHadThisParticipantAliases(participant, normOrg);
            if (isCurrentlySelected) {
                next.delete(canonicalKey);
                aliases.forEach((a) => next.delete(a));
            } else {
                next.add(canonicalKey);
            }
            return next;
        });
    };

    const computeEqualShares = useCallback((
        selectedCanonicalKeys: string[],
        existingShares: Record<string, any> = {}
    ) => {
        const shareMap: Record<string, { fraction: number; name?: string; mode: 'equal' | 'custom'; isLocked?: boolean }> = {};
        
        let lockedFractionSum = 0;
        const lockedKeys: string[] = [];
        const unlockedKeys: string[] = [];

        selectedCanonicalKeys.forEach((key) => {
            const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, key, normOrg));
            const locked = pObj ? isParticipantLocked(pObj) : Boolean(existingShares[key]?.isLocked || existingShares[key]?.paid);
            if (locked) {
                lockedKeys.push(key);
                const origFrac = pObj ? getLockedParticipantFraction(pObj) : getShareFraction(existingShares[key]);
                lockedFractionSum += origFrac;
                shareMap[key] = {
                    fraction: origFrac,
                    name: pObj?.name || existingShares[key]?.name || (key === normOrg ? 'Organizer' : undefined),
                    mode: (existingShares[key]?.mode as any) || 'equal',
                    isLocked: true,
                };
            } else {
                unlockedKeys.push(key);
            }
        });

        // Retain any existing locked shares that were not in selectedCanonicalKeys
        Object.entries(existingShares).forEach(([shareKey, s]: [string, any]) => {
            const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, shareKey, normOrg));
            const canonicalKey = pObj ? getWhoHadThisParticipantCanonicalKey(pObj, normOrg) : (normalizePhone(shareKey) || shareKey);
            if (!shareMap[canonicalKey]) {
                const locked = pObj ? isParticipantLocked(pObj) : Boolean(s?.isLocked || s?.paid);
                if (locked && getShareFraction(s) > 0.0001) {
                    const origFrac = getShareFraction(s);
                    lockedFractionSum += origFrac;
                    shareMap[canonicalKey] = {
                        fraction: origFrac,
                        name: pObj?.name || s?.name || (canonicalKey === normOrg ? 'Organizer' : undefined),
                        mode: (s?.mode as any) || 'equal',
                        isLocked: true,
                    };
                }
            }
        });

        const remainingFraction = Math.max(0, 1.0 - lockedFractionSum);
        const unlockedCount = unlockedKeys.length;

        if (unlockedCount > 0) {
            const equalFrac = Number((remainingFraction / unlockedCount).toFixed(4));
            let running = 0;
            unlockedKeys.forEach((key, idx) => {
                const isLast = idx === unlockedKeys.length - 1;
                const frac = isLast ? Number((remainingFraction - running).toFixed(4)) : equalFrac;
                running += frac;
                const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, key, normOrg));
                shareMap[key] = {
                    fraction: Math.max(0, frac),
                    name: pObj?.name || (key === normOrg ? 'Organizer' : undefined),
                    mode: 'equal',
                    isLocked: false,
                };
            });
        }

        return shareMap;
    }, [getLockedParticipantFraction, isParticipantLocked, normOrg, participants]);

    const handleSelectAllToggle = () => {
        triggerUiHaptic('selection');
        const selectableParticipants = participants.filter((p) => !isParticipantDeclined(p) && !isParticipantLocked(p));
        const allSelectableSelected = selectableParticipants.length > 0 && selectableParticipants.every((p) => isParticipantInSelectedSet(p, selectedPhoneSet));

        if (allSelectableSelected) {
            // Deselect unlocked participants; keep existing locked ones
            const next = new Set<string>();
            participants.forEach((p) => {
                if (isParticipantLocked(p) && isParticipantInSelectedSet(p, selectedPhoneSet)) {
                    next.add(getWhoHadThisParticipantCanonicalKey(p, normOrg));
                }
            });
            setSelectedPhoneSet(next);
        } else {
            const next = new Set(selectedPhoneSet);
            selectableParticipants.forEach((p) => {
                next.add(getWhoHadThisParticipantCanonicalKey(p, normOrg));
            });
            setSelectedPhoneSet(next);
        }
    };


    const selectedCount = selectedParticipants.length;
    const amountEach = selectedCount > 0 ? Number((itemAmount / selectedCount).toFixed(2)) : 0;
    const viewerParticipant = participants.find((p) => matchesWhoHadThisParticipant(p, normViewer, normOrg));
    const viewerIsSelected = Boolean(viewerParticipant && isParticipantInSelectedSet(viewerParticipant, selectedPhoneSet));

    // Hero amount editor handlers
    const handleStartEditParticipant = (p: WhoHadThisParticipant) => {
        if (isParticipantLocked(p)) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({
                title: 'Participant Locked',
                message: `${p.name || 'This participant'} has already paid and their share cannot be modified.`,
            });
            return;
        }
        triggerUiHaptic('selection');
        const canonicalKey = getWhoHadThisParticipantCanonicalKey(p, normOrg);
        const aliases = getWhoHadThisParticipantAliases(p, normOrg);
        let currentVal = customDollarAmounts[canonicalKey];
        if (!currentVal) {
            for (const a of aliases) {
                if (customDollarAmounts[a]) {
                    currentVal = customDollarAmounts[a];
                    break;
                }
            }
        }
        if (!currentVal) {
            currentVal = selectedCount > 0 ? (itemAmount / selectedCount).toFixed(2) : '0.00';
        }
        setEditingAmountText(currentVal);
        setEditingParticipant(p);
        setTimeout(() => {
            amountInputRef.current?.focus();
            amountInputRef.current?.select();
        }, 80);
    };

    const handleDoneEditParticipant = () => {
        if (!editingParticipant) return;
        triggerUiHaptic('impactLight');
        const canonicalKey = getWhoHadThisParticipantCanonicalKey(editingParticipant, normOrg);
        const cleaned = editingAmountText.replace(/[^0-9.]/g, '');
        const parsed = parseFloat(cleaned) || 0;
        const formatted = parsed > 0 ? parsed.toFixed(2) : '0.00';
        setCustomDollarAmounts((prev) => ({
            ...prev,
            [canonicalKey]: formatted,
        }));
        setEditingParticipant(null);
    };

    const handleAmountTextChange = (val: string) => {
        const cleaned = val.replace(/[^0-9.]/g, '');
        const parts = cleaned.split('.');
        if (parts.length > 2) return;
        if (parts[1] && parts[1].length > 2) return;
        setEditingAmountText(cleaned);
    };

    const otherParticipantsTotal = useMemo(() => {
        if (!editingParticipant) return 0;
        const editKey = getWhoHadThisParticipantCanonicalKey(editingParticipant, normOrg);
        let sum = hiddenLockedCents / 100;
        selectedParticipants.forEach((p) => {
            const canonicalKey = getWhoHadThisParticipantCanonicalKey(p, normOrg);
            if (canonicalKey !== editKey) {
                sum += isParticipantLocked(p) ? Math.round(getLockedParticipantFraction(p) * totalCents) / 100 : parseFloat(customDollarAmounts[canonicalKey] || '0') || 0;
            }
        });
        return sum;
    }, [customDollarAmounts, editingParticipant, getLockedParticipantFraction, hiddenLockedCents, isParticipantLocked, normOrg, selectedParticipants, totalCents]);

    const availableRemainderForEditing = Math.max(0, itemAmount - otherParticipantsTotal);

    const handleRemoveMe = () => {
        if (!currentItem || !normViewer) return;
        triggerUiHaptic('impactLight');
        const viewerP = participants.find((p) => matchesWhoHadThisParticipant(p, normViewer, normOrg));
        const canonicalViewer = viewerP ? getWhoHadThisParticipantCanonicalKey(viewerP, normOrg) : normViewer;
        const aliases = viewerP ? getWhoHadThisParticipantAliases(viewerP, normOrg) : [normViewer];

        const next = new Set(selectedPhoneSet);
        next.delete(canonicalViewer);
        aliases.forEach((a) => next.delete(a));
        setSelectedPhoneSet(next);

        const remainingSelected = participants.filter((p) => isParticipantInSelectedSet(p, next));
        const shareMap = computeEqualShares(
            remainingSelected.map((p) => getWhoHadThisParticipantCanonicalKey(p, normOrg)),
            currentItem.shares || {}
        );

        onSaveAssignment({
            itemId: currentItem.id,
            shareMap,
        });
        handleSafeClose();
    };

    const handleSave = () => {
        if (!currentItem) return;
        triggerUiHaptic('impactMedium');
        const activeParticipants = selectedParticipants;

        if (activeParticipants.length === 0) {
            // Retain any locked shares
            const existingShares = currentItem.shares || {};
            const preservedLockedMap: Record<string, { fraction: number; name?: string; mode: 'equal' | 'custom'; isLocked?: boolean }> = {};
            Object.entries(existingShares).forEach(([shareKey, s]: [string, any]) => {
                const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, shareKey, normOrg));
                const canonicalKey = pObj ? getWhoHadThisParticipantCanonicalKey(pObj, normOrg) : (normalizePhone(shareKey) || shareKey);
                const locked = pObj ? isParticipantLocked(pObj) : Boolean(s?.isLocked || s?.paid);
                if (locked && getShareFraction(s) > 0.0001) {
                    preservedLockedMap[canonicalKey] = {
                        fraction: getShareFraction(s),
                        name: pObj?.name || s?.name || (canonicalKey === normOrg ? 'Organizer' : undefined),
                        mode: (s?.mode as any) || 'equal',
                        isLocked: true,
                    };
                }
            });
            onSaveAssignment({
                itemId: currentItem.id,
                shareMap: preservedLockedMap,
            });
            handleSafeClose();
            return;
        }

        let shareMap: Record<string, { fraction: number; name?: string; mode: 'equal' | 'custom'; isLocked?: boolean }> = {};

        if (!isCustomMode) {
            shareMap = computeEqualShares(
                activeParticipants.map((p) => getWhoHadThisParticipantCanonicalKey(p, normOrg)),
                currentItem.shares || {}
            );
        } else {
            // Validate aggregate custom allocations do not exceed item total
            let totalCustomDollars = 0;
            activeParticipants.forEach((p) => {
                if (isParticipantLocked(p)) {
                    totalCustomDollars += Number(((getLockedParticipantFraction(p) * Math.round(itemAmount * 100)) / 100).toFixed(2));
                } else {
                    const phone = getWhoHadThisParticipantCanonicalKey(p, normOrg);
                    const aliases = getWhoHadThisParticipantAliases(p, normOrg);
                    let rawVal = 0;
                    for (const a of [phone, ...aliases]) {
                        if (customDollarAmounts[a]) {
                            rawVal = parseFloat(customDollarAmounts[a]) || 0;
                            break;
                        }
                    }
                    totalCustomDollars += rawVal;
                }
            });

            if (itemAmount > 0 && totalCustomDollars > itemAmount + 0.02) {
                triggerUiHaptic('notificationWarning');
                setAlertModal({
                    title: 'Total Exceeded',
                    message: `Total allocated amount ($${totalCustomDollars.toFixed(2)}) cannot exceed the item total ($${itemAmount.toFixed(2)}).`,
                });
                return;
            }

            activeParticipants.forEach((p) => {
                const phone = getWhoHadThisParticipantCanonicalKey(p, normOrg);
                const isLocked = isParticipantLocked(p);
                if (isLocked) {
                    const origFrac = getLockedParticipantFraction(p);
                    shareMap[phone] = {
                        fraction: origFrac,
                        name: p.name || (phone === normOrg ? 'Organizer' : undefined),
                        mode: 'custom',
                        isLocked: true,
                    };
                } else {
                    const aliases = getWhoHadThisParticipantAliases(p, normOrg);
                    let rawVal = 0;
                    for (const a of [phone, ...aliases]) {
                        if (customDollarAmounts[a]) {
                            rawVal = parseFloat(customDollarAmounts[a]) || 0;
                            break;
                        }
                    }
                    const frac = itemAmount > 0 ? Number((rawVal / itemAmount).toFixed(4)) : 0;
                    if (frac > 0.0001) {
                        shareMap[phone] = {
                            fraction: Math.min(1.0, Math.max(0, frac)),
                            name: p.name || (phone === normOrg ? 'Organizer' : undefined),
                            mode: 'custom',
                            isLocked: false,
                        };
                    }
                }
            });

            // Retain any authoritative locked shares on this item not in activeParticipants
            const existingShares = currentItem.shares || {};
            Object.entries(existingShares).forEach(([shareKey, s]: [string, any]) => {
                const pObj = participants.find((p) => matchesWhoHadThisParticipant(p, shareKey, normOrg));
                const canonicalKey = pObj ? getWhoHadThisParticipantCanonicalKey(pObj, normOrg) : (normalizePhone(shareKey) || shareKey);
                if (!shareMap[canonicalKey]) {
                    const locked = pObj ? isParticipantLocked(pObj) : Boolean(s?.isLocked || s?.paid);
                    if (locked && getShareFraction(s) > 0.0001) {
                        const origFrac = getShareFraction(s);
                        totalCustomDollars += Number((origFrac * itemAmount).toFixed(2));
                        shareMap[canonicalKey] = {
                            fraction: origFrac,
                            name: pObj?.name || s?.name || (canonicalKey === normOrg ? 'Organizer' : undefined),
                            mode: (s?.mode as any) || 'custom',
                            isLocked: true,
                        };
                    }
                }
            });
            if (Math.round(totalCustomDollars * 100) > Math.round(itemAmount * 100)) {
                triggerUiHaptic('notificationWarning');
                setAlertModal({ title: 'Total Exceeded', message: 'Assigned amounts cannot exceed the item total.' });
                return;
            }
            shareMap = reconcileFullCustomShareRemainder(shareMap, totalCustomDollars, itemAmount);
        }

        if (Object.values(shareMap).reduce((sum, share) => sum + share.fraction, 0) > 1.0001) {
            triggerUiHaptic('notificationWarning');
            setAlertModal({ title: 'Total Exceeded', message: 'Assigned shares cannot exceed the item total.' });
            return;
        }

        onSaveAssignment({
            itemId: currentItem.id,
            shareMap,
        });
        handleSafeClose();
    };

    if (!isRendered) return null;
    if (!currentItem) return null;

    const nonDeclinedParticipants = participants.filter((p) => !isParticipantDeclined(p));
    const allSelected = nonDeclinedParticipants.length > 0 && nonDeclinedParticipants.every((p) => isParticipantInSelectedSet(p, selectedPhoneSet));

    return (
        <div className={`fixed inset-0 z-[80] bg-slate-950/60 backdrop-blur-sm flex flex-col justify-end sm:items-center sm:justify-center p-0 sm:p-4 overscroll-contain ${
            isClosing ? 'animate-backdrop-exit pointer-events-none' : 'animate-backdrop-enter'
        }`}>
            <div className="absolute inset-0" onClick={handleSafeClose} />
            <div 
                className={`relative w-full max-w-lg bg-white rounded-t-[2.25rem] sm:rounded-[2.25rem] flex flex-col max-h-[90vh] overflow-hidden overscroll-contain ${
                    isClosing ? 'animate-sheet-exit' : 'animate-sheet-enter'
                }`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* 1. Header (Switches when editing individual participant) */}
                {editingParticipant ? (
                    <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3 min-w-0 pr-4">
                            <WebHeaderIconButton
                                onClick={() => setEditingParticipant(null)}
                                aria-label="Back to participants"
                            />
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-extrabold text-slate-900 truncate leading-tight">
                                    Edit amount
                                </h3>
                                <p className="text-xs font-semibold text-slate-500 mt-0.5 truncate">
                                    {formatShortName(editingParticipant.name, normalizePhone(editingParticipant.phone || editingParticipant.id) === normViewer)}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={handleDoneEditParticipant}
                            className="w-9 h-9 rounded-full bg-teal-600 hover:bg-teal-700 text-white flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                            aria-label="Done"
                        >
                            <Check className="w-5 h-5 stroke-[2.4]" />
                        </button>
                    </div>
                ) : (
                    <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-3.5 min-w-0 pr-4">
                            <WebItemIconAvatar name={currentItem.name} iconKey={currentItem.iconKey} size={44} />
                            <div className="min-w-0 flex-1">
                                <h3 className="text-base font-extrabold text-slate-900 truncate leading-tight">
                                    {currentItem.name || 'Item'}
                                </h3>
                                <p className="text-sm font-black text-slate-900 mt-0.5">
                                    ${itemAmount.toFixed(2)}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleSafeClose}
                            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                            aria-label="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                )}

                {/* 2. Body Content */}
                <div className="flex-1 overflow-y-auto overscroll-contain px-6 py-5 space-y-6">
                    {editingParticipant ? (
                        /* Hero Amount Editor Sub-View */
                        <div className="space-y-6 py-2">
                            {/* Selected Participant Badge Card */}
                            {(() => {
                                const resolved = resolveWebParticipantIdentity({
                                    participant: editingParticipant,
                                    viewerPhone,
                                    viewerDisplayName,
                                    viewerPhotoURL,
                                    organizerPhone,
                                    organizerName,
                                    organizerPhotoURL,
                                    session,
                                });
                                const editAmountNum = parseFloat(editingAmountText || '0') || 0;
                                const pct = itemAmount > 0 ? Math.min(100, Math.round((editAmountNum / itemAmount) * 100)) : 0;

                                return (
                                    <div className="bg-slate-50 rounded-2xl p-3.5 flex items-center justify-between gap-3">
                                         <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-9 h-9 rounded-full bg-[#D7E1EA] text-[#475569] font-black text-xs flex items-center justify-center shrink-0 overflow-hidden">
                                                {resolved.photoURL ? (
                                                    <img src={resolved.photoURL} alt={resolved.canonicalName} className="w-full h-full object-cover" />
                                                ) : (
                                                    <span>{resolved.initials}</span>
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-extrabold text-slate-900 truncate">
                                                    {resolved.isViewer ? 'You' : resolved.canonicalName}
                                                </p>
                                                <p className="text-xs font-semibold text-slate-500 mt-0.5">
                                                    Total item: ${itemAmount.toFixed(2)}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full bg-white font-bold text-xs text-slate-700 shadow-2xs">
                                            {pct}%
                                        </span>
                                    </div>
                                );
                            })()}

                            {/* Centered Large Hero Amount Input */}
                            <div className="flex flex-col items-center justify-center py-4">
                                <div className="flex items-center justify-center relative max-w-full">
                                    <span className="text-4xl font-black text-slate-400 mr-1">$</span>
                                    <input
                                        ref={amountInputRef}
                                        type="text"
                                        inputMode="decimal"
                                        value={editingAmountText}
                                        onChange={(e) => handleAmountTextChange(e.target.value)}
                                        placeholder="0.00"
                                        className="text-5xl font-black text-slate-900 text-center bg-transparent border-none outline-none max-w-[260px] tracking-tight placeholder:text-slate-300"
                                    />
                                    {editingAmountText && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setEditingAmountText('');
                                                amountInputRef.current?.focus();
                                            }}
                                            className="ml-2 w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors cursor-pointer"
                                            aria-label="Clear amount"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Shortcut Chips */}
                            <div className="flex items-center justify-center gap-2 flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => {
                                        triggerUiHaptic('impactLight');
                                        setEditingAmountText(availableRemainderForEditing.toFixed(2));
                                    }}
                                    className="px-4 py-2 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200/80 text-xs font-bold text-slate-700 transition-all active:scale-95 cursor-pointer"
                                >
                                    Remaining (${availableRemainderForEditing.toFixed(2)})
                                </button>
                                {editableParticipants.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            triggerUiHaptic('impactLight');
                                            setEditingAmountText((remainingDollars / editableParticipants.length).toFixed(2));
                                        }}
                                        className="px-4 py-2 rounded-full bg-slate-50 hover:bg-slate-100 border border-slate-200/80 text-xs font-bold text-slate-700 transition-all active:scale-95 cursor-pointer"
                                    >
                                        Split equally (${(remainingDollars / editableParticipants.length).toFixed(2)})
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        /* People Carousel & Assignment List */
                        <>
                            {/* Section Header */}
                            <div className="flex items-center justify-between">
                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                                    SELECT PEOPLE
                                </span>
                                {participants.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleSelectAllToggle}
                                        className="text-xs font-bold text-teal-700 hover:text-teal-800 transition-colors cursor-pointer"
                                    >
                                        {allSelected ? 'Clear all' : 'Select all'}
                                    </button>
                                )}
                            </div>

                            {/* Horizontal Scroll Carousel of 56px Circular Avatars */}
                            <div className="overflow-x-auto pt-2.5 pb-2 -mx-6 px-6 no-scrollbar flex items-start gap-4">
                                {sortedParticipants.map((p) => {
                                    const canonicalKey = getWhoHadThisParticipantCanonicalKey(p, normOrg);
                                    const isMe = Boolean(normViewer && matchesWhoHadThisParticipant(p, normViewer, normOrg));
                                    const isSelected = isParticipantInSelectedSet(p, selectedPhoneSet);
                                    const isLocked = isParticipantLocked(p);
                                    const isDeclined = isParticipantDeclined(p);
                                    const isDimmed = isLocked || isDeclined;
                                    const resolved = resolveWebParticipantIdentity({
                                        participant: p,
                                        viewerPhone,
                                        viewerDisplayName,
                                        viewerPhotoURL,
                                        organizerPhone,
                                        organizerName,
                                        organizerPhotoURL,
                                        session,
                                    });
                                    const shortName = isMe ? 'You' : formatShortName(resolved.canonicalName || p.name, false);

                                    return (
                                        <button
                                            key={canonicalKey}
                                            type="button"
                                            onClick={() => handleToggleParticipant(p)}
                                            className={`flex flex-col items-center gap-1.5 shrink-0 group transition-transform active:scale-95 w-[68px] cursor-pointer ${
                                                isDimmed ? 'opacity-45' : ''
                                            }`}
                                        >
                                            <div className="relative">
                                                <div
                                                    className={`w-14 h-14 rounded-full flex items-center justify-center font-bold text-base transition-all overflow-hidden border-2 ${
                                                        isSelected
                                                            ? 'border-teal-600 bg-slate-100 text-slate-800'
                                                            : 'border-transparent bg-slate-100 text-slate-700'
                                                    }`}
                                                >
                                                    {resolved.photoURL ? (
                                                        <img src={resolved.photoURL} alt={resolved.canonicalName || p.name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <span>{resolved.initials}</span>
                                                    )}
                                                </div>

                                                {/* Selected Teal Badge on Bottom Right */}
                                                {isSelected && !isLocked && (
                                                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-teal-600 text-white flex items-center justify-center border-2 border-white shadow-xs">
                                                        <Check className="w-3 h-3 stroke-[3]" />
                                                    </div>
                                                )}

                                                {/* Locked / Declined Badge */}
                                                {isLocked && (
                                                    <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center border-2 border-white shadow-xs">
                                                        <Lock className="w-2.5 h-2.5 stroke-[2.4]" />
                                                    </div>
                                                )}
                                            </div>

                                            <span className={`text-[12px] truncate max-w-[64px] text-center leading-tight ${
                                                isSelected && !isLocked
                                                    ? 'text-teal-700 font-bold'
                                                    : isSelected && isLocked
                                                    ? 'text-teal-700 font-bold'
                                                    : isLocked
                                                    ? 'text-slate-400 font-medium'
                                                    : 'text-slate-600 font-bold'
                                            }`}>
                                                {shortName}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Locked Notice Card */}
                            {lockedPhoneSet.size > 0 && (
                                <div className="flex items-center gap-2 p-3 rounded-2xl bg-slate-100/70 border border-slate-200/80 text-slate-600 text-xs font-semibold">
                                    <Lock className="w-3.5 h-3.5 text-slate-500 stroke-[2.2] shrink-0" />
                                    <span>Paid participants are locked and can’t be changed.</span>
                                </div>
                            )}

                            {/* Split Mode Summary Card */}
                            {selectedCount > 0 && !isCustomMode && (
                                <div className="bg-slate-50 rounded-2xl p-4 flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <p className="font-extrabold text-sm text-slate-900">
                                            Split equally
                                        </p>
                                        <p className="text-xs font-medium text-slate-500 mt-0.5 truncate">
                                            {equalSplitSubtitle}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            triggerUiHaptic('impactMedium');
                                            setIsCustomMode(true);
                                        }}
                                        className="px-4 py-2 rounded-full bg-white text-xs font-bold text-slate-700 hover:bg-slate-100 transition-colors shrink-0 shadow-2xs cursor-pointer"
                                    >
                                        Change
                                    </button>
                                </div>
                            )}

                            {/* Custom Mode Editor */}
                            {selectedCount > 0 && isCustomMode && (() => {
                                const totalAllocated = (Array.from(selectedPhoneSet) as string[]).reduce<number>((sum, phone) => sum + (parseFloat(customDollarAmounts[phone] || '0') || 0), 0);
                                const remainingAmount = Math.max(0, itemAmount - totalAllocated);

                                return (
                                    <div className="space-y-2.5 bg-slate-50 rounded-2xl p-4">
                                        <div className="flex items-center justify-between pb-1">
                                            <span className="text-sm font-bold text-slate-900">Custom amounts</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    triggerUiHaptic('impactMedium');
                                                    setIsCustomMode(false);
                                                }}
                                                className="text-xs font-semibold text-teal-700 hover:text-teal-800 transition-colors cursor-pointer"
                                            >
                                                Split evenly instead
                                            </button>
                                        </div>

                                        <div className="divide-y divide-transparent space-y-1">
                                            {selectedParticipants.map((p) => {
                                                const canonicalKey = getWhoHadThisParticipantCanonicalKey(p, normOrg);
                                                const aliases = getWhoHadThisParticipantAliases(p, normOrg);
                                                const isMe = Boolean(normViewer && matchesWhoHadThisParticipant(p, normViewer, normOrg));
                                                const resolved = resolveWebParticipantIdentity({
                                                    participant: p,
                                                    viewerPhone,
                                                    viewerDisplayName,
                                                    viewerPhotoURL,
                                                    organizerPhone,
                                                    organizerName,
                                                    organizerPhotoURL,
                                                    session,
                                                });
                                                const name = isMe ? 'You' : formatShortName(resolved.canonicalName || p?.name || 'Friend', false);
                                                const isLocked = isParticipantLocked(p);
                                                const lockedFrac = isLocked ? getLockedParticipantFraction(p) : 0;
                                                let customValStr = customDollarAmounts[canonicalKey];
                                                if (!customValStr) {
                                                    for (const a of aliases) {
                                                        if (customDollarAmounts[a]) {
                                                            customValStr = customDollarAmounts[a];
                                                            break;
                                                        }
                                                    }
                                                }
                                                const numericVal = isLocked
                                                    ? Number(((lockedFrac * Math.round(itemAmount * 100)) / 100).toFixed(2))
                                                    : (parseFloat(customValStr || '0') || 0);
                                                const pct = itemAmount > 0 ? Math.round((numericVal / itemAmount) * 100) : 0;

                                                return (
                                                    <div 
                                                        key={canonicalKey} 
                                                        className={`flex items-center justify-between py-1.5 gap-3 ${
                                                            isLocked ? 'opacity-60' : ''
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                                            <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 font-bold text-xs flex items-center justify-center shrink-0 overflow-hidden border border-white">
                                                                {resolved.photoURL ? (
                                                                    <img src={resolved.photoURL} alt={name} className="w-full h-full object-cover" />
                                                                ) : (
                                                                    <span>{resolved.initials}</span>
                                                                )}
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-[15px] font-semibold text-slate-900 truncate leading-tight">{name}</p>
                                                                <p className="text-xs text-slate-400 font-normal mt-0.5">{isLocked ? 'Paid' : `${pct}% of item`}</p>
                                                            </div>
                                                        </div>

                                                        <button
                                                            type="button"
                                                            onClick={() => handleStartEditParticipant(p)}
                                                            className={`px-3.5 py-1.5 rounded-full min-w-[72px] flex items-center justify-center font-bold text-sm transition-all cursor-pointer ${
                                                                isLocked
                                                                    ? 'bg-slate-200/80 text-slate-500'
                                                                    : 'bg-white hover:bg-slate-100 text-slate-900 shadow-2xs active:scale-95'
                                                            }`}
                                                        >
                                                            {isLocked && <Lock className="w-3 h-3 text-slate-500 mr-1 stroke-[2.2]" />}
                                                            <span>${numericVal.toFixed(2)}</span>
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Remaining / Left for Group to Claim */}
                                        <div className="border-t border-slate-200/70 pt-3 mt-2 space-y-0.5">
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-semibold text-slate-700">Left for group to claim</span>
                                                <span className="text-sm font-bold text-slate-900">
                                                    ${remainingAmount.toFixed(2)}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-400 font-normal">
                                                {remainingAmount <= 0.001
                                                    ? 'Item is fully assigned.'
                                                    : 'Remaining amount can be claimed by participants later.'}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>

                {/* Footer Actions */}
                {!editingParticipant && (
                    <div className="px-6 py-4 border-t border-slate-100 bg-white flex items-center gap-3">
                        {viewerIsSelected && (
                            <button
                                type="button"
                                onClick={handleRemoveMe}
                                className="h-12 min-h-[48px] px-6 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-extrabold transition-all cursor-pointer"
                            >
                                Remove me
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleSave}
                            className="flex-1 h-12 min-h-[48px] rounded-full bg-teal-600 hover:bg-teal-700 text-white text-sm font-extrabold transition-all text-center flex items-center justify-center cursor-pointer"
                        >
                            Save assignment
                        </button>
                    </div>
                )}
            </div>

            {/* Alert Modal Popup for Locked/Declined interactions */}
            {alertModal && (
                <div className="fixed inset-0 z-[100] bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 animate-backdrop-enter">
                    <div className="w-full max-w-sm bg-white rounded-3xl p-6 shadow-2xl space-y-4 animate-sheet-enter">
                        <div className="space-y-1.5 text-center">
                            <h4 className="text-base font-extrabold text-slate-900">{alertModal.title}</h4>
                            <p className="text-xs font-medium text-slate-500 leading-relaxed">{alertModal.message}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setAlertModal(null)}
                            className="w-full h-11 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold transition-all active:scale-[0.98] cursor-pointer"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WebWhoHadThisSheet;

